// 商品資訊
'use client';

import { useState, useEffect } from 'react';
import dayjs from '@/common/dayjs';
import anaClient from '@/common/apiClient/anaClient';

import ExchangeItemInfo from '@/components/exchange/ExchangeItemInfo'; // 兌換商品資訊
import DrawItemInfo from '@/components/draw/DrawItemInfo'; // 抽獎商品資訊
import ItemAmount from '@/components/item/ItemAmount'; // 商品數量
import ItemBottomButton from '@/components/item/ItemBottomButton'; // 手機置底按鈕

interface Props {
    item: any;
}

const ItemInfoWrapper = ({ item }: Props) => {
    const {
        code,
        type = 1,
        beginTime,
        endTime,
        redeemable = true,
        remainingStock,
        winnerCount = 0,
        redeemLimit,
        myRedeemedQuantity = 0,
        maxTicketsPerUser,
        myTicketCount = 0,
    } = item || {};

    // 記錄商品內頁瀏覽（供熱門兌換商品依當天 PV 排序使用）
    useEffect(() => {
        if (code) anaClient.createItemView(code).catch((e) => console.error(e));
    }, [code]);

    const isDrawType = type === 4; // 判斷是否為抽獎商品

    // 投入的抽獎券數（兌換類型固定為 1）
    const [amount, setAmount] = useState(1);

    const now = dayjs();
    const isNotStarted = beginTime && now.isBefore(dayjs(beginTime)); // 尚未開始
    const isEnd = endTime && now.isAfter(dayjs(endTime)); // 已結束：純粹以結束時間判斷
    // 庫存不足：抽獎類型改用 winnerCount（抽獎人數）判斷，兌換類型 null 代表無庫存限制
    const isOutOfStock = isDrawType ? !winnerCount : remainingStock === 0;
    // 兌換類型：redeemLimit 為 null/0 代表可無限兌換，個人兌換次數達上限才算達標
    const isRedeemLimitReached = !!redeemLimit && myRedeemedQuantity >= redeemLimit;
    // 兌換類型：個人尚未達兌換上限，但已無庫存 → 已全數兌換完畢
    const isSoldOut = !isRedeemLimitReached && isOutOfStock;
    // 抽獎類型：maxTicketsPerUser 為 null 代表無限制，個人投入抽獎券數達上限才算達標
    const isTicketLimitReached = maxTicketsPerUser != null && myTicketCount >= maxTicketsPerUser;

    const isDisabled = isDrawType
        ? isNotStarted || isEnd || !redeemable || isOutOfStock || isTicketLimitReached
        : isNotStarted || isEnd || isRedeemLimitReached || isSoldOut;

    // 按鈕文字
    const buttonLabel = (() => {
        if (isNotStarted) return '即將開始';
        if (isDrawType) {
            if (isEnd || !redeemable || isOutOfStock) return '已結束';
            if (isTicketLimitReached) return '已達投入上限';
            return '參加抽獎';
        }
        if (isEnd) return '兌換已結束';
        if (isRedeemLimitReached) return '已達兌換上限';
        if (isSoldOut) return '已全數兌換完畢';
        return '確定兌換';
    })();

    return (
        <div className="w-full grow min-h-full space-y-5 px-4 lg:pt-3 lg:px-0 lg:space-y-10">
            {/* 兌換、抽獎 */}
            {isDrawType ? (
                <DrawItemInfo item={item} amount={amount} setAmount={setAmount} />
            ) : (
                <ExchangeItemInfo item={item} isDisabled={isDisabled} buttonLabel={buttonLabel} />
            )}

            {/* 商品數量 */}
            <ItemAmount item={item} isDrawType={isDrawType} />

            {/* 手機置底按鈕 */}
            <ItemBottomButton
                item={item}
                isDrawType={isDrawType}
                amount={amount}
                isDisabled={isDisabled}
                buttonLabel={buttonLabel}
            />
        </div>
    );
};

export default ItemInfoWrapper;
